import { useSelector } from 'react-redux';
import { useCallback, useMemo, useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import compact from 'lodash/compact';
import formFieldsSelectors from '../store/selectors/form-fields.selectors';
import { PORTAL_TYPE } from '../types/app.types';
import { useParamsContext } from '../params/params-context';
import { kbId } from '../network/services/get-form-fields/get-form-fields.service';

const useSmartFaq = (): {
  initFAQ: (e: CustomEvent) => void;
  event?: CustomEvent<unknown>;
  sourceConfig: { baseTag?: string; kbId: number };
} => {
  const [event, setEvent] = useState<CustomEvent<unknown>>();

  const { user, portalType } = useParamsContext();

  const { caseReasonTag, caseDetailedReasonTag } = useSelector(
    formFieldsSelectors.getReasonsTags
  );

  const { t } = useTranslation();

  const faqTag = useMemo(() => {
    return caseDetailedReasonTag || caseReasonTag;
  }, [caseDetailedReasonTag, caseReasonTag]);

  const sourceConfig = useMemo(() => {
    switch (portalType) {
      case PORTAL_TYPE.ER1:
        return user
          ? { baseTag: 'er1-1351', kbId: 91 }
          : { baseTag: 'er1-public-744', kbId: 91 };
      case PORTAL_TYPE.FMER:
        return { baseTag: 'fmer-1255', kbId: 91 };
      case PORTAL_TYPE.UMER:
        return { baseTag: 'umer-public-755', kbId: 92 };
      case PORTAL_TYPE.ERDE:
        return { baseTag: undefined, kbId: 90 };
      default:
        return { baseTag: undefined, kbId: 90 };
    }
  }, [portalType, user]);

  const labels = useMemo(() => {
    return {
      title: {
        [i18n.language]: t([
          `smartFaq.${portalType}.title`,
          'smartFaq.title',
          ''
        ])
      },
      emptyMessage: {
        [i18n.language]: t([
          `smartFaq.${portalType}.emptyMessage`,
          'smartFaq.emptyMessage',
          ''
        ])
      },
      openOnFaq: {
        [i18n.language]: t([
          `smartFaq.${portalType}.openOnFaq`,
          'smartFaq.openOnFaq',
          ''
        ])
      },
      goToFaqLink: {
        [i18n.language]: t([
          `smartFaq.${portalType}.goToFaqLink`,
          'smartFaq.goToFaqLink',
          ''
        ])
      }
    };
  }, [portalType, t]);

  const tagsArray = useMemo(() => {
    return compact([sourceConfig.baseTag, faqTag]).length
      ? compact([sourceConfig.baseTag, faqTag])
      : [''];
  }, [faqTag, sourceConfig.baseTag]);

  const initFAQ = useCallback(
    (e: CustomEvent): void => {
      setEvent(e);
      e.detail.init({
        kbId: kbId() ?? sourceConfig.kbId,
        locale: i18n.language,
        cookieOptin: false,
        filters: {
          thematics: [],
          tags: [...tagsArray]
        },
        labels,
        limit: 3,
        faqUrl: 'https://mybenefits.de/hilfe-kontakt-public',
        openInNewTab: true,
        containerId: 'st-faq-context',
        singleQuestionOpen: true
      });
    },
    [sourceConfig.kbId, tagsArray, labels]
  );

  return { initFAQ, event, sourceConfig };
};

export default useSmartFaq;
